// The top of the page: a sticky bar of section links over a full-width photo
// hero with the logo, a tagline and a "Next show" button.
import { useEffect, useRef, useState } from 'react';
import { Navbar, Nav } from 'react-bootstrap';
import { FaPen } from 'react-icons/fa';
import { SiteData } from './content';
import { FieldSpec, useImageSrc, useSite } from './content/context';
import { HeaderSettings, Section } from './content/layout';
import { ActionButton, ChangePhotoButton, EditableText } from './edit/primitives';
import { kitFormAction } from './sections/kit';
import { newsletterDisplay } from './sections/Newsletter';
import { splitShows } from './Shows';
import { hexToRgba, isDark } from './theme/theme';
import { safeHttpUrl } from './urls';

export const sectionAnchor = (section: Section) => `s-${section.id}`;

const LINK_LABELS: Partial<Record<Section['type'], string>> = {
  music: 'Music',
  shows: 'Shows',
  members: 'Band',
  gallery: 'Gallery',
  contact: 'Contact',
  newsletter: 'Newsletter',
};

const titleCase = (text: string) => text.toLowerCase().replace(/(^|\s)(\S)/g, (_m, space: string, c: string) => space + c.toUpperCase());

/** One link per visible section (except the bio, which is right under the header). */
export function headerLinks(data: SiteData): { href: string; label: string }[] {
  return data.layout.sections
    .filter((s) => !s.hidden && s.type !== 'bio')
    .filter((s) => s.type !== 'newsletter' || (kitFormAction(s.formId) && newsletterDisplay(s) !== 'popup'))
    .filter((s) => s.type !== 'contact' || Boolean(data.site.contactEmail))
    .map((s) => ({ href: `#${sectionAnchor(s)}`, label: LINK_LABELS[s.type] ?? (s.title ? titleCase(s.title) : '') }))
    .filter((l) => l.label)
    .slice(0, 7);
}

/** The photo, fading into the page background at the bottom so the logo and text stay readable. */
export function heroBackground(pageBackground: string, photo?: string): string {
  const shade = (a: number) => hexToRgba(pageBackground, a);
  return photo
    ? `linear-gradient(180deg, ${shade(0.25)} 0%, ${shade(0.3)} 35%, ${shade(0.8)} 62%, ${shade(1)} 100%), url("${encodeURI(photo)}")`
    : `linear-gradient(180deg, ${shade(0.6)}, ${shade(1)})`;
}

const formatShortDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const yesNo = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const settingsFields: FieldSpec[] = [
  { name: 'showNextShow', label: 'Show a "Next show" button when a show is coming up?', type: 'select', options: yesNo },
  { name: 'buttonLabel', label: 'Extra button text', placeholder: 'Listen now', hint: 'Optional. Leave empty for no extra button.' },
  {
    name: 'buttonUrl',
    label: 'Extra button link',
    type: 'url',
    placeholder: 'https://',
    validate: (v) => (!v || safeHttpUrl(v) ? undefined : 'Enter a full link starting with https://'),
  },
  { name: 'showLinks', label: 'Show the bar of links to each section?', type: 'select', options: yesNo },
  {
    name: 'height',
    label: 'Header height',
    type: 'select',
    options: [
      { value: 'tall', label: 'Tall' },
      { value: 'medium', label: 'Medium' },
      { value: 'short', label: 'Short' },
    ],
  },
];

export default function SiteHeader() {
  const { data, edit } = useSite();
  const imageSrc = useImageSrc();
  const { header, theme } = data.layout;
  const heroRef = useRef<HTMLElement>(null);
  const [pastHero, setPastHero] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // The link bar sits transparently over the photo, then turns solid once you scroll past it.
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setPastHero(!entry.isIntersecting), { rootMargin: '-64px 0px 0px 0px' });
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  const save = (patch: Partial<HeaderSettings>) => edit?.update('layout', { ...data.layout, header: { ...header, ...patch } });

  const editSettings = async () => {
    if (!edit) return;
    const result = await edit.openForm({
      title: 'Header settings',
      fields: settingsFields,
      values: {
        showNextShow: header.showNextShow ? 'yes' : 'no',
        showLinks: header.showLinks ? 'yes' : 'no',
        buttonLabel: header.buttonLabel,
        buttonUrl: header.buttonUrl,
        height: header.height,
      },
    });
    if (!result || !('values' in result)) return;
    const v = result.values;
    save({
      showNextShow: v.showNextShow === 'yes',
      showLinks: v.showLinks === 'yes',
      buttonLabel: v.buttonLabel.trim() || undefined,
      buttonUrl: v.buttonUrl.trim() || undefined,
      height: v.height === 'medium' || v.height === 'short' ? v.height : 'tall',
    });
  };

  const links = headerLinks(data);
  const logo = imageSrc(data.site.logo) ?? '/images/arc-flash-logo.png';
  const photo = imageSrc(header.image);
  const heroStyle = { backgroundImage: heroBackground(theme.pageBackground, photo) };

  const next = header.showNextShow ? splitShows(data.shows, new Date()).upcoming[0] : undefined;
  const nextHref = next ? safeHttpUrl(next.ticketUrl) : undefined;
  const showsSection = data.layout.sections.find((s) => s.type === 'shows');
  const extraHref = safeHttpUrl(header.buttonUrl);
  const solid = pastHero || menuOpen || Boolean(edit);

  return (
    <>
      {header.showLinks && links.length ? (
        <Navbar
          expand="md"
          variant={isDark(theme.pageBackground) ? 'dark' : 'light'}
          className={`af-header-bar${solid ? ' af-solid' : ''}${edit ? ' af-static' : ''}`}
          expanded={menuOpen}
          onToggle={setMenuOpen}
          aria-label="Sections"
        >
          <div className="container">
            <Navbar.Brand href="#top" className={`af-bar-logo${pastHero ? ' af-show' : ''}`} aria-hidden={!pastHero} tabIndex={pastHero ? 0 : -1}>
              <img src={logo} alt="Arc Flash" />
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="af-header-links" aria-label="Menu" />
            <Navbar.Collapse id="af-header-links" className="justify-content-end">
              <Nav>
                {links.map((link) => (
                  <Nav.Link key={link.href} href={link.href} onClick={() => setMenuOpen(false)}>
                    {link.label}
                  </Nav.Link>
                ))}
              </Nav>
            </Navbar.Collapse>
          </div>
        </Navbar>
      ) : null}

      <header id="top" ref={heroRef} className={`af-hero af-hero-${header.height}`} style={heroStyle}>
        <div className="af-hero-content">
          <h1 className="af-hero-logo">
            <img src={logo} alt="Arc Flash" />
          </h1>
          {header.tagline || edit ? (
            <EditableText as="p" className="af-hero-tagline" label="Header tagline" placeholder="Add a tagline" value={header.tagline} onChange={(tagline) => save({ tagline })} />
          ) : null}
          {next || (header.buttonLabel && extraHref) ? (
            <div className="af-hero-actions">
              {next ? (
                <a
                  className="btn btn-lg af-button"
                  href={nextHref ?? (showsSection ? `#${sectionAnchor(showsSection)}` : '#')}
                  {...(nextHref ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  Next show: {formatShortDate(next.when)} · {next.venue}
                </a>
              ) : null}
              {header.buttonLabel && extraHref ? (
                <a className="btn btn-lg af-button-outline" href={extraHref} target="_blank" rel="noopener noreferrer">
                  {header.buttonLabel}
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
        {edit ? (
          <>
            <ChangePhotoButton label="Change header photo" left onChange={(image) => save({ image })} />
            <div className="af-actions">
              <ActionButton
                label="Change logo"
                icon={<FaPen />}
                showLabel
                onClick={async () => {
                  const path = await edit.chooseImage();
                  if (path) edit.update('site', { ...data.site, logo: path });
                }}
              />
              <ActionButton label="Header settings" icon={<FaPen />} showLabel onClick={editSettings} />
            </div>
          </>
        ) : null}
      </header>
    </>
  );
}
